import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface OrderItem {
  id: number;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  quantity: number;
  size?: string;
  selectedColor?: {
    name: string;
    hex: string;
  };
}

export interface SizeExchangeDetail {
  itemIndex: number;
  itemName: string;
  orderedSize: string;
  deliveredSize: string;
}

export interface ReturnItem {
  itemName: string;
  itemImage: string;
  quantity: number;
  reason: string;
}

export interface Order {
  id: string;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  customer_city: string;
  customer_pincode?: string;
  customer_lat?: number;
  customer_lng?: number;
  items: OrderItem[];
  total_amount: number;
  wallet_discount: number;
  final_amount: number;
  payment_method: string;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  order_type: 'instant' | 'cart';
  delivery_otp?: string;
  otp_verified?: boolean;
  delivery_lat?: number;
  delivery_lng?: number;
  delivery_boy_name?: string;
  delivery_boy_phone?: string;
  collected_payment_method?: 'cash' | 'online';
  payment_collected_at?: string;
  size_exchanged?: boolean;
  size_exchange_details?: SizeExchangeDetail[];
  return_requested?: boolean;
  return_reason?: string;
  return_items?: ReturnItem[];
  return_requested_at?: string;
  created_at: string;
  updated_at: string;
}

export async function saveOrder(order: Omit<Order, 'created_at' | 'updated_at'>): Promise<{ success: boolean; error?: string }> {
  // Normalize phone number before saving to ensure consistent format
  const normalizedOrder = {
    ...order,
    customer_phone: order.customer_phone.replace(/\D/g, '').replace(/^(91|0)/, ''),
  };
  const { error } = await supabase.from('orders').insert([normalizedOrder]);
  if (error) {
    console.error('Order save error:', error);
    return { success: false, error: error.message };
  }
  
  // Send WhatsApp notification to admin
  try {
    await sendWhatsAppOrderNotification(order);
  } catch (e) {
    console.error('WhatsApp notification error:', e);
    // Don't fail order if WhatsApp fails
  }
  
  return { success: true };
}

// Send WhatsApp notification to admin about new order
export async function sendWhatsAppOrderNotification(order: Omit<Order, 'created_at' | 'updated_at'>): Promise<{ success: boolean; error?: string }> {
  const edgeFunctionUrl = `${supabaseUrl}/functions/v1/whatsapp-admin-order`;
  
  const payload = {
    order_id: order.id,
    customer_name: order.customer_name,
    customer_phone: order.customer_phone,
    customer_address: order.customer_address,
    customer_city: order.customer_city,
    items: order.items.map(item => ({
      name: item.name,
      quantity: item.quantity,
      price: item.price,
    })),
    final_amount: order.final_amount,
    payment_method: order.payment_method,
  };
  
  try {
    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify(payload),
    });
    
    const result = await response.json();
    
    if (!response.ok || !result.success) {
      // Silently fail - don't block order placement for WhatsApp notification issues
      console.warn('WhatsApp notification skipped:', result.error || 'Admin number not configured');
      return { success: true }; // Return success to prevent order failure
    }
    
    console.log('WhatsApp notification sent successfully');
    return { success: true };
  } catch (e) {
    console.error('WhatsApp notification error:', e);
    return { success: false, error: e instanceof Error ? e.message : 'Network error' };
  }
}

export async function fetchAllOrders(): Promise<Order[]> {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) {
    console.error('Fetch orders error:', error);
    return [];
  }
  return (data as Order[]) ?? [];
}

// Fetch only active orders (pending, confirmed, shipped) — for home screen polling
export async function fetchActiveOrders(): Promise<Order[]> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .in('status', ['pending', 'confirmed', 'shipped'])
      .order('created_at', { ascending: false });
    if (error) {
      // Silent fail for polling — network errors are transient
      return [];
    }
    return (data as Order[]) ?? [];
  } catch {
    // Network error — return empty silently (polling will retry)
    return [];
  }
}

// Fetch orders by status with pagination (20 per page)
export async function fetchOrdersByStatus(
  status: 'shipped' | 'delivered' | 'cancelled',
  page: number = 0,
  pageSize: number = 20,
  dateFrom?: string,
  dateTo?: string
): Promise<{ data: Order[]; hasMore: boolean; total: number }> {
  const from = page * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('orders')
    .select('*', { count: 'exact' })
    .eq('status', status)
    .order('created_at', { ascending: false });

  if (dateFrom) query = query.gte('created_at', dateFrom);
  if (dateTo) query = query.lte('created_at', dateTo);

  const { data, error, count } = await query.range(from, to);

  if (error) {
    console.error('Fetch orders by status error:', error);
    return { data: [], hasMore: false, total: 0 };
  }
  const total = count ?? 0;
  return {
    data: (data as Order[]) ?? [],
    hasMore: from + pageSize < total,
    total,
  };
}

// Fetch order counts for stats (lightweight — no items data needed)
export async function fetchOrderCounts(
  dateFrom?: string,
  dateTo?: string
): Promise<Record<Order['status'], number>> {
  let query = supabase.from('orders').select('status');
  if (dateFrom) query = query.gte('created_at', dateFrom);
  if (dateTo) query = query.lte('created_at', dateTo);

  const { data, error } = await query;
  if (error) return { pending: 0, confirmed: 0, shipped: 0, delivered: 0, cancelled: 0 };
  const counts: Record<Order['status'], number> = { pending: 0, confirmed: 0, shipped: 0, delivered: 0, cancelled: 0 };
  (data ?? []).forEach((row: { status: Order['status'] }) => { counts[row.status] = (counts[row.status] ?? 0) + 1; });
  return counts;
}

// Fetch active orders with optional date filter
export async function fetchActiveOrdersFiltered(
  dateFrom?: string,
  dateTo?: string
): Promise<Order[]> {
  try {
    let query = supabase
      .from('orders')
      .select('*')
      .in('status', ['pending', 'confirmed', 'shipped'])
      .order('created_at', { ascending: false });

    if (dateFrom) query = query.gte('created_at', dateFrom);
    if (dateTo) query = query.lte('created_at', dateTo);

    const { data, error } = await query;
    if (error) {
      return [];
    }
    return (data as Order[]) ?? [];
  } catch {
    return [];
  }
}

// Helper: get date range ISO strings for a given period
export function getDateRange(period: 'today' | 'week' | 'month' | 'all'): { from?: string; to?: string } {
  const now = new Date();
  const toStr = new Date(now.getTime() + 1000).toISOString(); // +1s to include current moment

  if (period === 'all') return {};

  if (period === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: toStr };
  }

  if (period === 'week') {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: toStr };
  }

  if (period === 'month') {
    const start = new Date(now);
    start.setDate(now.getDate() - 29);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: toStr };
  }

  return {};
}

export async function updateOrderStatus(
  orderId: string,
  status: Order['status']
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('orders')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', orderId);
  if (error) {
    return { success: false, error: error.message };
  }
  return { success: true };
}

export async function fetchOrdersByPhone(phone: string): Promise<Order[]> {
  // Normalize phone number before querying to ensure consistent matching
  const normalizedPhone = phone.replace(/\D/g, '').replace(/^(91|0)/, '');
  const { data, error } = await supabase
    .from('orders')
    .select('id,customer_name,customer_phone,customer_address,customer_city,customer_pincode,customer_lat,customer_lng,items,total_amount,wallet_discount,final_amount,payment_method,status,order_type,delivery_otp,otp_verified,delivery_lat,delivery_lng,delivery_boy_name,delivery_boy_phone,collected_payment_method,return_requested,created_at,updated_at')
    .eq('customer_phone', normalizedPhone)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) {
    console.error('Fetch orders by phone error:', error);
    return [];
  }
  return (data as Order[]) ?? [];
}

// Fetch orders by email (for email-based login users)
export async function fetchOrdersByEmail(email: string): Promise<Order[]> {
  if (!email || !email.includes('@')) return [];
  // email users have phone stored as "email_<email>" in DB
  const emailPhone = `email_${email.trim().toLowerCase()}`;
  const { data, error } = await supabase
    .from('orders')
    .select('id,customer_name,customer_phone,customer_address,customer_city,customer_pincode,customer_lat,customer_lng,items,total_amount,wallet_discount,final_amount,payment_method,status,order_type,delivery_otp,otp_verified,delivery_lat,delivery_lng,delivery_boy_name,delivery_boy_phone,collected_payment_method,return_requested,created_at,updated_at')
    .eq('customer_phone', emailPhone)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) {
    console.error('Fetch orders by email error:', error);
    return [];
  }
  return (data as Order[]) ?? [];
}

// ─── Notifications ───────────────────────────────────────────────────────────

export interface CustomerNotification {
  id: string;
  order_id: string;
  customer_phone: string;
  customer_name: string;
  type: 'order_confirmed' | 'order_shipped' | 'order_delivered' | 'order_cancelled';
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

const NOTIFICATION_CONTENT: Record<
  CustomerNotification['type'],
  (name: string, orderId: string, extra?: string) => { title: string; message: string }
> = {
  order_confirmed: (name, orderId) => ({
    title: 'Order Confirmed!',
    message: `Hi ${name}! Your order #${orderId.toUpperCase()} has been confirmed. We are preparing it for you.`,
  }),
  order_shipped: (name, orderId) => ({
    title: 'Order Shipped!',
    message: `Great news ${name}! Your order #${orderId.toUpperCase()} is on its way. Out for delivery soon!`,
  }),
  order_delivered: (name, orderId) => ({
    title: 'Order Delivered!',
    message: `Your order #${orderId.toUpperCase()} has been delivered successfully. Thank you for shopping with ClothyBazar!`,
  }),
  order_cancelled: (name, orderId, reason) => ({
    title: 'Order Cancelled',
    message: `Hi ${name}, your order #${orderId.toUpperCase()} has been cancelled.${reason ? ` Reason: ${reason}` : ''} We apologize for the inconvenience.`,
  }),
};

export async function saveNotification(
  order: Order,
  type: CustomerNotification['type'],
  cancelReason?: string
): Promise<void> {
  const content = NOTIFICATION_CONTENT[type](order.customer_name, order.id, cancelReason);
  // Normalize phone number before saving to ensure consistent format
  const normalizedPhone = order.customer_phone.replace(/\D/g, '').replace(/^(91|0)/, '');
  const { error } = await supabase.from('notifications').insert([{
    order_id: order.id,
    customer_phone: normalizedPhone,
    customer_name: order.customer_name,
    type,
    title: content.title,
    message: content.message,
    is_read: false,
  }]);
  if (error) console.error('Save notification error:', error);
}

export async function fetchNotificationsByPhone(phone: string): Promise<CustomerNotification[]> {
  // Normalize phone number before querying to ensure consistent matching
  const normalizedPhone = phone.replace(/\D/g, '').replace(/^(91|0)/, '');
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('customer_phone', normalizedPhone)
    .order('created_at', { ascending: false })
    .limit(30);
  if (error) {
    console.error('Fetch notifications error:', error);
    return [];
  }
  return (data as CustomerNotification[]) ?? [];
}

export async function markNotificationsRead(phone: string): Promise<void> {
  await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('customer_phone', phone)
    .eq('is_read', false);
}

export async function markOneNotificationRead(id: string): Promise<void> {
  await supabase.from('notifications').update({ is_read: true }).eq('id', id);
}

// ─── Delivery OTP ─────────────────────────────────────────────────────────────

export function generateOTP(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

export async function saveDeliveryOTP(orderId: string, otp: string): Promise<{ success: boolean }> {
  const { error } = await supabase
    .from('orders')
    .update({ delivery_otp: otp, otp_verified: false, updated_at: new Date().toISOString() })
    .eq('id', orderId);
  if (error) {
    console.error('Save OTP error:', error);
    return { success: false };
  }
  return { success: true };
}

// ─── Delivery Boy Functions ───────────────────────────────────────────────────

export async function fetchShippedOrders(): Promise<Order[]> {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .in('status', ['confirmed', 'shipped'])
    .order('created_at', { ascending: false });
  if (error) return [];
  return (data as Order[]) ?? [];
}

export async function updateDeliveryLocation(
  orderId: string,
  lat: number,
  lng: number
): Promise<void> {
  await supabase
    .from('orders')
    .update({ delivery_lat: lat, delivery_lng: lng, updated_at: new Date().toISOString() })
    .eq('id', orderId);
}

export async function markOrderShipped(
  orderId: string,
  deliveryBoyName: string,
  deliveryBoyPhone: string,
  otp: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('orders')
    .update({
      status: 'shipped',
      delivery_boy_name: deliveryBoyName,
      delivery_boy_phone: deliveryBoyPhone,
      delivery_otp: otp,
      otp_verified: false,
      updated_at: new Date().toISOString(),
    })
    .eq('id', orderId);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function fetchOrderById(orderId: string): Promise<Order | null> {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('id', orderId)
    .maybeSingle();
  if (error || !data) return null;
  return data as Order;
}

// ─── Analytics Functions ──────────────────────────────────────────────────────

export interface ProductSalesData {
  id: number;
  name: string;
  image: string;
  category: string;
  totalQuantity: number;
  totalRevenue: number;
  orderCount: number;
  todayQty: number;
  weekQty: number;
  monthQty: number;
}

export interface SalesSummary {
  totalOrders: number;
  totalRevenue: number;
  avgOrderValue: number;
}

export interface AnalyticsData {
  today: SalesSummary;
  week: SalesSummary;
  month: SalesSummary;
  allTime: SalesSummary;
  topProducts: ProductSalesData[];
}

async function fetchDeliveredOrdersForPeriod(dateFrom?: string, dateTo?: string): Promise<Order[]> {
  let query = supabase
    .from('orders')
    .select('id,customer_name,customer_phone,items,final_amount,created_at,status,delivery_boy_name')
    .eq('status', 'delivered')
    .order('created_at', { ascending: false });
  if (dateFrom) query = query.gte('created_at', dateFrom);
  if (dateTo) query = query.lte('created_at', dateTo);
  const { data, error } = await query;
  if (error) return [];
  return (data as Order[]) ?? [];
}

// For Sales Report: fetch delivered orders for a period with a reasonable limit
export async function fetchDeliveredOrdersForReport(
  dateFrom?: string,
  dateTo?: string,
  limit: number = 500
): Promise<Order[]> {
  let query = supabase
    .from('orders')
    .select('id,customer_name,customer_phone,items,final_amount,created_at,status')
    .eq('status', 'delivered')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (dateFrom) query = query.gte('created_at', dateFrom);
  if (dateTo) query = query.lte('created_at', dateTo);
  const { data, error } = await query;
  if (error) {
    console.error('Fetch delivered orders for report error:', error);
    return [];
  }
  return (data as Order[]) ?? [];
}

function computeSummary(orders: Order[]): SalesSummary {
  const totalRevenue = orders.reduce((s, o) => s + o.final_amount, 0);
  return {
    totalOrders: orders.length,
    totalRevenue,
    avgOrderValue: orders.length > 0 ? Math.round(totalRevenue / orders.length) : 0,
  };
}

function computeTopProducts(orders: Order[]): ProductSalesData[] {
  const map = new Map<number, ProductSalesData>();
  for (const order of orders) {
    for (const item of order.items) {
      const existing = map.get(item.id);
      if (existing) {
        existing.totalQuantity += item.quantity;
        existing.totalRevenue += item.price * item.quantity;
        existing.orderCount += 1;
      } else {
        map.set(item.id, {
          id: item.id,
          name: item.name,
          image: item.image,
          category: item.category,
          totalQuantity: item.quantity,
          totalRevenue: item.price * item.quantity,
          orderCount: 1,
        });
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => b.totalQuantity - a.totalQuantity).slice(0, 10);
}

export async function fetchAnalyticsData(): Promise<AnalyticsData> {
  const now = new Date();
  const toStr = new Date(now.getTime() + 1000).toISOString();

  const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - 6); weekStart.setHours(0, 0, 0, 0);
  const monthStart = new Date(now); monthStart.setDate(now.getDate() - 29); monthStart.setHours(0, 0, 0, 0);

  const [todayOrders, weekOrders, monthOrders, allOrders] = await Promise.all([
    fetchDeliveredOrdersForPeriod(todayStart.toISOString(), toStr),
    fetchDeliveredOrdersForPeriod(weekStart.toISOString(), toStr),
    fetchDeliveredOrdersForPeriod(monthStart.toISOString(), toStr),
    fetchDeliveredOrdersForPeriod(),
  ]);

  // Build per-product qty maps for each period
  const qtyMap = (orders: Order[]): Map<number, number> => {
    const m = new Map<number, number>();
    for (const o of orders) {
      for (const item of o.items) {
        m.set(item.id, (m.get(item.id) ?? 0) + item.quantity);
      }
    }
    return m;
  };

  const todayMap = qtyMap(todayOrders);
  const weekMap = qtyMap(weekOrders);
  const monthMap = qtyMap(monthOrders);

  // Build top products from allOrders, enriched with period qty
  const map = new Map<number, ProductSalesData>();
  for (const order of allOrders) {
    for (const item of order.items) {
      const existing = map.get(item.id);
      if (existing) {
        existing.totalQuantity += item.quantity;
        existing.totalRevenue += item.price * item.quantity;
        existing.orderCount += 1;
      } else {
        map.set(item.id, {
          id: item.id,
          name: item.name,
          image: item.image,
          category: item.category,
          totalQuantity: item.quantity,
          totalRevenue: item.price * item.quantity,
          orderCount: 1,
          todayQty: 0,
          weekQty: 0,
          monthQty: 0,
        });
      }
    }
  }

  // Attach period-wise qty
  map.forEach((product) => {
    product.todayQty = todayMap.get(product.id) ?? 0;
    product.weekQty = weekMap.get(product.id) ?? 0;
    product.monthQty = monthMap.get(product.id) ?? 0;
  });

  const topProducts = Array.from(map.values())
    .sort((a, b) => b.totalQuantity - a.totalQuantity)
    .slice(0, 10);

  return {
    today: computeSummary(todayOrders),
    week: computeSummary(weekOrders),
    month: computeSummary(monthOrders),
    allTime: computeSummary(allOrders),
    topProducts,
  };
}

// ─── Revenue Chart Data ───────────────────────────────────────────────────────

export interface ChartDayData {
  label: string;      // e.g. "Mon", "12 Apr"
  date: string;       // ISO date string for tooltip
  revenue: number;
  orders: number;
}

export async function fetchRevenueChartData(
  mode: 'daily' | 'weekly'
): Promise<ChartDayData[]> {
  const now = new Date();

  if (mode === 'daily') {
    // Last 7 days — one bar per day
    const days: ChartDayData[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const end = new Date(d);
      end.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        .from('orders')
        .select('final_amount')
        .eq('status', 'delivered')
        .gte('created_at', d.toISOString())
        .lte('created_at', end.toISOString());

      const orders = error ? [] : (data ?? []);
      const revenue = orders.reduce((s: number, o: { final_amount: number }) => s + o.final_amount, 0);

      days.push({
        label: d.toLocaleDateString('en-IN', { weekday: 'short' }),
        date: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        revenue,
        orders: orders.length,
      });
    }
    return days;
  } else {
    // Last 4 weeks — one bar per week
    const weeks: ChartDayData[] = [];
    for (let i = 3; i >= 0; i--) {
      const end = new Date(now);
      end.setDate(now.getDate() - i * 7);
      end.setHours(23, 59, 59, 999);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from('orders')
        .select('final_amount')
        .eq('status', 'delivered')
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      const orders = error ? [] : (data ?? []);
      const revenue = orders.reduce((s: number, o: { final_amount: number }) => s + o.final_amount, 0);
      const weekLabel = i === 0 ? 'This Week' : i === 1 ? 'Last Week' : `${i * 7}d ago`;

      weeks.push({
        label: weekLabel,
        date: `${start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`,
        revenue,
        orders: orders.length,
      });
    }
    return weeks;
  }
}

export async function verifyOTPOnly(
  orderId: string,
  enteredOtp: string
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase
    .from('orders')
    .select('delivery_otp, otp_verified')
    .eq('id', orderId)
    .maybeSingle();

  if (error || !data) return { success: false, error: 'Order not found' };
  if (data.otp_verified) return { success: true };
  if (data.delivery_otp !== enteredOtp) return { success: false, error: 'Galat OTP hai. Dobara try karo.' };
  return { success: true };
}

export async function collectPaymentAndDeliver(
  orderId: string,
  paymentMethod: 'cash' | 'online'
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('orders')
    .update({
      status: 'delivered',
      otp_verified: true,
      collected_payment_method: paymentMethod,
      payment_collected_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', orderId);

  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function verifyAndDeliver(
  orderId: string,
  enteredOtp: string
): Promise<{ success: boolean; error?: string }> {
  // Fetch the order to check OTP
  const { data, error } = await supabase
    .from('orders')
    .select('delivery_otp, otp_verified')
    .eq('id', orderId)
    .maybeSingle();

  if (error || !data) return { success: false, error: 'Order not found' };
  if (data.otp_verified) return { success: true }; // already verified
  if (data.delivery_otp !== enteredOtp) return { success: false, error: 'Incorrect OTP. Please try again.' };

  // OTP correct — mark delivered
  const { error: updateError } = await supabase
    .from('orders')
    .update({ status: 'delivered', otp_verified: true, updated_at: new Date().toISOString() })
    .eq('id', orderId);

  if (updateError) return { success: false, error: updateError.message };
  return { success: true };
}

// ─── Payment Records for Delivery Boy ────────────────────────────────────────

export interface PaymentRecord {
  id: string;
  customer_name: string;
  customer_phone: string;
  final_amount: number;
  collected_payment_method: 'cash' | 'online';
  payment_collected_at: string;
  delivery_boy_name: string;
  delivery_boy_phone: string;
}

// ─── Complaints ──────────────────────────────────────────────────────────────

export interface Complaint {
  id: string;
  name: string;
  phone: string;
  complaint_type: string;
  order_id?: string;
  message: string;
  status: 'new' | 'in_progress' | 'resolved';
  created_at: string;
}

export async function saveComplaint(complaint: Omit<Complaint, 'id' | 'created_at' | 'status'>): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.from('complaints').insert([complaint]);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function fetchComplaints(): Promise<Complaint[]> {
  const { data, error } = await supabase
    .from('complaints')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) return [];
  return (data as Complaint[]) ?? [];
}

export async function updateComplaintStatus(
  id: string,
  status: Complaint['status']
): Promise<{ success: boolean }> {
  const { error } = await supabase.from('complaints').update({ status }).eq('id', id);
  if (error) return { success: false };
  return { success: true };
}

// ─── Delivery Boy Stats ───────────────────────────────────────────────────────

export interface DeliveryBoyStats {
  boyName: string;
  boyPhone: string;
  totalDelivered: number;
  cashTotal: number;
  cashCount: number;
  onlineTotal: number;
  onlineCount: number;
  sizeExchangeCount: number;
  returnCount: number;
}

export async function fetchDeliveryBoyStats(
  deliveryBoyPhone: string,
  period: 'today' | 'month' | 'all'
): Promise<DeliveryBoyStats | null> {
  const now = new Date();
  let fromDate: string | undefined;
  if (period === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    fromDate = start.toISOString();
  } else if (period === 'month') {
    const start = new Date(now); start.setDate(now.getDate() - 29); start.setHours(0, 0, 0, 0);
    fromDate = start.toISOString();
  }

  let query = supabase
    .from('orders')
    .select('id, customer_name, delivery_boy_name, delivery_boy_phone, final_amount, collected_payment_method, size_exchanged, size_exchange_details, return_requested, return_items, return_reason')
    .eq('status', 'delivered')
    .eq('delivery_boy_phone', deliveryBoyPhone);

  if (fromDate) query = query.gte('payment_collected_at', fromDate);

  const { data, error } = await query;
  if (error || !data || data.length === 0) return null;

  const stats: DeliveryBoyStats = {
    boyName: data[0].delivery_boy_name || '',
    boyPhone: deliveryBoyPhone,
    totalDelivered: data.length,
    cashTotal: 0, cashCount: 0,
    onlineTotal: 0, onlineCount: 0,
    sizeExchangeCount: 0,
    returnCount: 0,
  };

  data.forEach((o: { final_amount: number; collected_payment_method?: string; size_exchanged?: boolean; return_requested?: boolean }) => {
    if (o.collected_payment_method === 'cash') { stats.cashTotal += o.final_amount; stats.cashCount++; }
    else if (o.collected_payment_method === 'online') { stats.onlineTotal += o.final_amount; stats.onlineCount++; }
    if (o.size_exchanged) stats.sizeExchangeCount++;
    if (o.return_requested) stats.returnCount++;
  });

  return stats;
}

export async function fetchAllDeliveryBoysStats(
  period: 'today' | 'month' | 'all'
): Promise<DeliveryBoyStats[]> {
  const now = new Date();
  let fromDate: string | undefined;
  if (period === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    fromDate = start.toISOString();
  } else if (period === 'month') {
    const start = new Date(now); start.setDate(now.getDate() - 29); start.setHours(0, 0, 0, 0);
    fromDate = start.toISOString();
  }

  let query = supabase
    .from('orders')
    .select('id, delivery_boy_name, delivery_boy_phone, final_amount, collected_payment_method, size_exchanged, return_requested')
    .eq('status', 'delivered')
    .not('delivery_boy_phone', 'is', null);

  if (fromDate) query = query.gte('payment_collected_at', fromDate);

  const { data, error } = await query;
  if (error || !data) return [];

  const map = new Map<string, DeliveryBoyStats>();
  data.forEach((o: { delivery_boy_name?: string; delivery_boy_phone?: string; final_amount: number; collected_payment_method?: string; size_exchanged?: boolean; return_requested?: boolean }) => {
    if (!o.delivery_boy_phone) return;
    if (!map.has(o.delivery_boy_phone)) {
      map.set(o.delivery_boy_phone, {
        boyName: o.delivery_boy_name || o.delivery_boy_phone,
        boyPhone: o.delivery_boy_phone,
        totalDelivered: 0, cashTotal: 0, cashCount: 0,
        onlineTotal: 0, onlineCount: 0,
        sizeExchangeCount: 0, returnCount: 0,
      });
    }
    const s = map.get(o.delivery_boy_phone)!;
    s.totalDelivered++;
    if (o.collected_payment_method === 'cash') { s.cashTotal += o.final_amount; s.cashCount++; }
    else if (o.collected_payment_method === 'online') { s.onlineTotal += o.final_amount; s.onlineCount++; }
    if (o.size_exchanged) s.sizeExchangeCount++;
    if (o.return_requested) s.returnCount++;
  });

  return Array.from(map.values()).sort((a, b) => b.totalDelivered - a.totalDelivered);
}

export async function saveReturnRequest(
  orderId: string,
  returnItems: ReturnItem[],
  returnReason: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('orders')
    .update({
      return_requested: true,
      return_reason: returnReason,
      return_items: returnItems,
      return_requested_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', orderId);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

// Cancel order by delivery boy — marks as cancelled + saves return/rejection details
export async function cancelOrderByDeliveryBoy(
  orderId: string,
  returnItems: ReturnItem[],
  returnReason: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('orders')
    .update({
      status: 'cancelled',
      return_requested: true,
      return_reason: returnReason,
      return_items: returnItems,
      return_requested_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', orderId);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

// ─── Customer Records for Admin ──────────────────────────────────────────────

export interface CustomerRecord {
  id: string;
  phone: string;
  email?: string;
  name: string;
  address_city?: string;
  registered_at: string;
  last_login_at?: string;
  wallet_balance: number;
  signup_bonus_given?: boolean;
}

export interface CustomerWithCart {
  customer: CustomerRecord;
  cartItems: Array<{
    id: number;
    name: string;
    price: number;
    image: string;
    category: string;
    quantity: number;
    size?: string;
    selectedColor?: { name: string; hex: string };
  }>;
}

export async function fetchAllCustomers(): Promise<CustomerRecord[]> {
  const { data, error } = await supabase
    .from('customers')
    .select('id,phone,email,name,address_city,registered_at,last_login_at,wallet_balance,signup_bonus_given')
    .order('registered_at', { ascending: false });
  if (error) { console.error('fetchAllCustomers error:', error); return []; }
  return (data as CustomerRecord[]) ?? [];
}

export async function fetchTodayCustomers(): Promise<CustomerRecord[]> {
  const now = new Date();
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from('customers')
    .select('id,phone,email,name,address_city,registered_at,last_login_at,wallet_balance,signup_bonus_given')
    .gte('last_login_at', start.toISOString())
    .order('last_login_at', { ascending: false });
  if (error) return [];
  return (data as CustomerRecord[]) ?? [];
}

export async function fetchTodayRegistrations(): Promise<CustomerRecord[]> {
  const now = new Date();
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from('customers')
    .select('id,phone,email,name,address_city,registered_at,last_login_at,wallet_balance,signup_bonus_given')
    .gte('registered_at', start.toISOString())
    .order('registered_at', { ascending: false });
  if (error) return [];
  return (data as CustomerRecord[]) ?? [];
}

export async function fetchRepeatCustomers(): Promise<Array<{ phone: string; name: string; order_count: number; total_spent: number; last_order: string }>> {
  const { data, error } = await supabase
    .from('orders')
    .select('customer_phone,customer_name,final_amount,created_at');
  if (error || !data) return [];

  const map = new Map<string, { phone: string; name: string; order_count: number; total_spent: number; last_order: string }>();
  (data as { customer_phone: string; customer_name: string; final_amount: number; created_at: string }[]).forEach(o => {
    const existing = map.get(o.customer_phone);
    if (existing) {
      existing.order_count++;
      existing.total_spent += o.final_amount;
      if (o.created_at > existing.last_order) existing.last_order = o.created_at;
    } else {
      map.set(o.customer_phone, { phone: o.customer_phone, name: o.customer_name, order_count: 1, total_spent: o.final_amount, last_order: o.created_at });
    }
  });

  return Array.from(map.values())
    .filter(c => c.order_count >= 2)
    .sort((a, b) => b.order_count - a.order_count);
}

// ─── Gift Codes ───────────────────────────────────────────────────────────────

export interface GiftCode {
  id: string;
  code: string;
  discount_type: 'flat' | 'percent';
  discount_value: number;
  min_order_amount: number;
  max_uses: number;
  used_count: number;
  is_active: boolean;
  expires_at?: string;
  used_by: string[];
  description?: string;
  created_at: string;
}

export async function fetchGiftCodes(): Promise<GiftCode[]> {
  const { data, error } = await supabase
    .from('gift_codes')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) return [];
  return (data as GiftCode[]) ?? [];
}

export async function createGiftCode(code: Omit<GiftCode, 'id' | 'created_at' | 'used_count' | 'used_by'>): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.from('gift_codes').insert([{ ...code, used_count: 0, used_by: [] }]);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function toggleGiftCodeStatus(id: string, is_active: boolean): Promise<{ success: boolean }> {
  const { error } = await supabase.from('gift_codes').update({ is_active }).eq('id', id);
  return { success: !error };
}

export async function validateGiftCode(
  code: string,
  orderTotal: number,
  customerPhone: string
): Promise<{ valid: boolean; discount: number; message: string; codeData?: GiftCode }> {
  const { data, error } = await supabase
    .from('gift_codes')
    .select('*')
    .eq('code', code.toUpperCase().trim())
    .maybeSingle();

  if (error || !data) return { valid: false, discount: 0, message: 'Invalid gift code' };
  const gc = data as GiftCode;

  if (!gc.is_active) return { valid: false, discount: 0, message: 'Yeh code active nahi hai' };
  if (gc.expires_at && new Date(gc.expires_at) < new Date()) return { valid: false, discount: 0, message: 'Yeh code expire ho gaya' };
  if (gc.used_count >= gc.max_uses) return { valid: false, discount: 0, message: 'Yeh code already use ho gaya hai' };
  if ((gc.used_by ?? []).includes(customerPhone)) return { valid: false, discount: 0, message: 'Aapne pehle hi yeh code use kar liya hai' };
  if (orderTotal < gc.min_order_amount) return { valid: false, discount: 0, message: `Minimum order ₹${gc.min_order_amount} chahiye` };

  const discount = gc.discount_type === 'percent'
    ? Math.floor((orderTotal * gc.discount_value) / 100)
    : gc.discount_value;

  return { valid: true, discount: Math.min(discount, orderTotal), message: `₹${Math.min(discount, orderTotal)} discount milega!`, codeData: gc };
}

export async function redeemGiftCode(
  codeId: string,
  customerPhone: string,
  currentUsedCount: number,
  currentUsedBy: string[]
): Promise<{ success: boolean }> {
  const { error } = await supabase
    .from('gift_codes')
    .update({
      used_count: currentUsedCount + 1,
      used_by: [...(currentUsedBy ?? []), customerPhone],
    })
    .eq('id', codeId);
  return { success: !error };
}

export async function giveSignupBonus(customerId: string, phone: string): Promise<{ success: boolean; error?: string }> {
  // Check if bonus already given
  const { data: existing } = await supabase
    .from('customers')
    .select('signup_bonus_given, wallet_balance, wallet_transactions')
    .eq('id', customerId)
    .maybeSingle();

  if (!existing) return { success: false, error: 'Customer not found' };
  if (existing.signup_bonus_given) return { success: false, error: 'Bonus already given' };

  const newBalance = (existing.wallet_balance ?? 0) + 50;
  const newTx = [...(existing.wallet_transactions ?? []), {
    type: 'bonus',
    amount: 50,
    description: 'Signup Bonus - Welcome to ClothyBazar!',
    timestamp: new Date().toISOString(),
  }];

  const { error } = await supabase
    .from('customers')
    .update({ wallet_balance: newBalance, wallet_transactions: newTx, signup_bonus_given: true })
    .eq('id', customerId);

  if (error) return { success: false, error: error.message };
  return { success: true };
}

// ─── Visitor Activity ────────────────────────────────────────────────────────

export interface VisitorActivity {
  id: string;
  session_id: string;
  page: string;
  product_name?: string;
  action: string;
  customer_phone?: string;
  customer_name?: string;
  device_type: string;
  city?: string;
  referrer?: string;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface VisitorStats {
  totalVisits: number;
  uniqueSessions: number;
  registeredVisits: number;
  guestVisits: number;
  mobileVisits: number;
  desktopVisits: number;
  topPages: Array<{ page: string; count: number }>;
  topCities: Array<{ city: string; count: number }>;
  hourlyActivity: Array<{ hour: number; count: number }>;
  recentVisitors: VisitorActivity[];
  todayVisits: number;
  weekVisits: number;
}

export async function fetchVisitorStats(): Promise<VisitorStats> {
  const now = new Date();
  const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - 6); weekStart.setHours(0, 0, 0, 0);

  // Fetch all recent activity (last 30 days for stats)
  const monthStart = new Date(now); monthStart.setDate(now.getDate() - 29); monthStart.setHours(0, 0, 0, 0);

  const { data: allData, error } = await supabase
    .from('visitor_activity')
    .select('*')
    .gte('created_at', monthStart.toISOString())
    .order('created_at', { ascending: false })
    .limit(2000);

  if (error || !allData) {
    return {
      totalVisits: 0, uniqueSessions: 0, registeredVisits: 0, guestVisits: 0,
      mobileVisits: 0, desktopVisits: 0, topPages: [], topCities: [],
      hourlyActivity: [], recentVisitors: [], todayVisits: 0, weekVisits: 0,
    };
  }

  const rows = allData as VisitorActivity[];

  const uniqueSessions = new Set(rows.map(r => r.session_id)).size;
  const registeredVisits = rows.filter(r => r.customer_phone).length;
  const guestVisits = rows.filter(r => !r.customer_phone).length;
  const mobileVisits = rows.filter(r => r.device_type === 'mobile').length;
  const desktopVisits = rows.filter(r => r.device_type === 'desktop' || r.device_type === 'tablet').length;

  // Today & week
  const todayVisits = rows.filter(r => r.created_at >= todayStart.toISOString()).length;
  const weekVisits = rows.filter(r => r.created_at >= weekStart.toISOString()).length;

  // Top pages
  const pageMap = new Map<string, number>();
  rows.forEach(r => pageMap.set(r.page, (pageMap.get(r.page) ?? 0) + 1));
  const topPages = Array.from(pageMap.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([page, count]) => ({ page, count }));

  // Top cities
  const cityMap = new Map<string, number>();
  rows.filter(r => r.city).forEach(r => cityMap.set(r.city!, (cityMap.get(r.city!) ?? 0) + 1));
  const topCities = Array.from(cityMap.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([city, count]) => ({ city, count }));

  // Hourly activity (today)
  const todayRows = rows.filter(r => r.created_at >= todayStart.toISOString());
  const hourMap = new Map<number, number>();
  todayRows.forEach(r => {
    const hour = new Date(r.created_at).getHours();
    hourMap.set(hour, (hourMap.get(hour) ?? 0) + 1);
  });
  const hourlyActivity = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: hourMap.get(i) ?? 0 }));

  // Recent visitors (last 50)
  const recentVisitors = rows.slice(0, 50);

  return {
    totalVisits: rows.length,
    uniqueSessions,
    registeredVisits,
    guestVisits,
    mobileVisits,
    desktopVisits,
    topPages,
    topCities,
    hourlyActivity,
    recentVisitors,
    todayVisits,
    weekVisits,
  };
}

export async function fetchLiveVisitors(): Promise<VisitorActivity[]> {
  const since = new Date(Date.now() - 15 * 60 * 1000).toISOString(); // last 15 min
  const { data, error } = await supabase
    .from('visitor_activity')
    .select('*')
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) return [];
  return (data as VisitorActivity[]) ?? [];
}

export async function fetchCustomerActivity(phone: string): Promise<VisitorActivity[]> {
  const normalized = phone.replace(/\D/g, '').replace(/^(91|0)/, '');
  const { data, error } = await supabase
    .from('visitor_activity')
    .select('*')
    .eq('customer_phone', normalized)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) return [];
  return (data as VisitorActivity[]) ?? [];
}

export interface ProductViewRecord {
  product_name: string;
  customer_name: string | null;
  customer_phone: string | null;
  action: string;
  city: string | null;
  device_type: string;
  created_at: string;
}

export async function fetchProductViews(days = 30): Promise<ProductViewRecord[]> {
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('visitor_activity')
    .select('product_name, customer_name, customer_phone, action, city, device_type, created_at')
    .eq('action', 'product_click')
    .not('product_name', 'is', null)
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) return [];
  return (data as ProductViewRecord[]) ?? [];
}

export async function fetchTopViewedProducts(days = 30): Promise<Array<{ product_name: string; view_count: number; unique_customers: number }>> {
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('visitor_activity')
    .select('product_name, customer_phone')
    .eq('action', 'product_click')
    .not('product_name', 'is', null)
    .gte('created_at', since);
  if (error || !data) return [];
  const map = new Map<string, { count: number; phones: Set<string> }>();
  for (const row of data as { product_name: string; customer_phone: string | null }[]) {
    if (!row.product_name) continue;
    if (!map.has(row.product_name)) map.set(row.product_name, { count: 0, phones: new Set() });
    const entry = map.get(row.product_name)!;
    entry.count++;
    if (row.customer_phone) entry.phones.add(row.customer_phone);
  }
  return Array.from(map.entries())
    .map(([product_name, { count, phones }]) => ({ product_name, view_count: count, unique_customers: phones.size }))
    .sort((a, b) => b.view_count - a.view_count)
    .slice(0, 30);
}

export async function saveSizeExchangeDetails(
  orderId: string,
  details: SizeExchangeDetail[]
): Promise<{ success: boolean }> {
  const hasExchange = details.some(d => d.orderedSize !== d.deliveredSize);
  const { error } = await supabase
    .from('orders')
    .update({
      size_exchanged: hasExchange,
      size_exchange_details: hasExchange ? details : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', orderId);
  if (error) return { success: false };
  return { success: true };
}

// ─── Customer Address Update ──────────────────────────────────────────────────

export async function updateCustomerAddress(
  phone: string,
  addressData: {
    fullName: string;
    address: string;
    city: string;
    pincode: string;
    lat?: number;
    lng?: number;
  }
): Promise<{ success: boolean; error?: string }> {
  const normalizedPhone = phone.replace(/\D/g, '').replace(/^(91|0)/, '');
  const { error } = await supabase
    .from('customers')
    .update({
      name: addressData.fullName,
      address_full: addressData.address,
      address_city: addressData.city,
      address_pincode: addressData.pincode,
      address_lat: addressData.lat ?? null,
      address_lng: addressData.lng ?? null,
    })
    .eq('phone', normalizedPhone);

  if (error) {
    console.error('updateCustomerAddress error:', error);
    return { success: false, error: error.message };
  }
  return { success: true };
}

// ─── Admin Roles & Users ─────────────────────────────────────────────────────

export interface AdminRole {
  id: string;
  name: string;
  description?: string;
  permissions: string[];
  color?: string;
  created_at: string;
}

export interface AdminUser {
  id: string;
  name: string;
  phone: string;
  email?: string;
  role_id?: string | null;
  role_name?: string;
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
}

export async function fetchAdminRoles(): Promise<AdminRole[]> {
  const { data, error } = await supabase
    .from('admin_roles')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) { console.error('fetchAdminRoles error:', error); return []; }
  return (data as AdminRole[]) ?? [];
}

export async function createAdminRole(
  role: Omit<AdminRole, 'id' | 'created_at'>
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.from('admin_roles').insert([role]);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function updateAdminRole(
  id: string,
  role: Partial<Omit<AdminRole, 'id' | 'created_at'>>
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.from('admin_roles').update(role).eq('id', id);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function deleteAdminRole(id: string): Promise<{ success: boolean; error?: string }> {
  // First unassign all users with this role
  await supabase.from('admin_users').update({ role_id: null, role_name: null }).eq('role_id', id);
  const { error } = await supabase.from('admin_roles').delete().eq('id', id);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  const { data, error } = await supabase
    .from('admin_users')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.error('fetchAdminUsers error:', error); return []; }
  return (data as AdminUser[]) ?? [];
}

export async function createAdminUser(
  user: Omit<AdminUser, 'id' | 'created_at' | 'is_active' | 'last_login_at'>
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.from('admin_users').insert([{ ...user, is_active: true }]);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function updateAdminUser(
  id: string,
  user: Partial<Omit<AdminUser, 'id' | 'created_at'>>
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.from('admin_users').update(user).eq('id', id);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function toggleAdminUserStatus(
  id: string,
  is_active: boolean
): Promise<{ success: boolean }> {
  const { error } = await supabase.from('admin_users').update({ is_active }).eq('id', id);
  return { success: !error };
}

export async function deleteAdminUser(id: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.from('admin_users').delete().eq('id', id);
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function fetchPaymentRecords(
  deliveryBoyPhone: string,
  period: 'today' | 'month' | 'all'
): Promise<PaymentRecord[]> {
  const now = new Date();
  let fromDate: string | undefined;

  if (period === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    fromDate = start.toISOString();
  } else if (period === 'month') {
    const start = new Date(now);
    start.setDate(now.getDate() - 29);
    start.setHours(0, 0, 0, 0);
    fromDate = start.toISOString();
  }

  let query = supabase
    .from('orders')
    .select('id, customer_name, customer_phone, final_amount, collected_payment_method, payment_collected_at, delivery_boy_name, delivery_boy_phone')
    .eq('status', 'delivered')
    .eq('delivery_boy_phone', deliveryBoyPhone)
    .not('collected_payment_method', 'is', null)
    .order('payment_collected_at', { ascending: false });

  if (fromDate) query = query.gte('payment_collected_at', fromDate);

  const { data, error } = await query;
  if (error) {
    console.error('Fetch payment records error:', error);
    return [];
  }
  return (data as PaymentRecord[]) ?? [];
}
